import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { skipToken } from '@reduxjs/toolkit/query'

import ActionButtons from 'components/action-buttons/action-buttons.component'
import BasicTabs from 'components/tabs/basic-tabs/basic-tabs.component'
import TabPanel from 'components/tabs/tab-panel/tab-panel.component'
import TwoEqualColumnLayout from 'layouts/two-equal-column-layout/two-equal-column-layout'
import {
  DetailField,
  detailTextFieldSx,
} from 'pages/address/address-details/customer-details/customer-detail-fields'
import {
  useDeleteExpenseMutation,
  useGetExpenseByIdQuery,
  useGetExpenseReferenceOptionsQuery,
  useUpdateExpenseMutation,
} from 'store/expenses/expenses.api'
import {
  Expense,
  ExpenseLineItem,
  ExpenseRelatedEntityType,
  ExpenseReferenceOptions,
} from 'store/expenses/expenses.types'
import { useGetPreSignedUrlMutation } from 'store/invoices/invoices.api'
import { Tab } from 'types'
import { getUserId } from 'utils/common'

import {
  EXPENSE_CATEGORIES,
  EXPENSE_RELATED_TO,
  categoryLabel,
} from '../payables.constants'
import { resolveUnitPrice, sumLineAmounts, formatMoneyDisplay } from '../payables.utils'
import PayableDocumentViewer from './payable-document-viewer'

const viewTabs: Tab[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'details', label: 'Details' },
]

const REFERENCE_OPTION_KEYS: Array<keyof ExpenseReferenceOptions> = [
  'Truck',
  'Trailer',
  'Driver',
]

const emptyLine = (): ExpenseLineItem => ({
  description: '',
  lineNote: null,
  quantity: null,
  unitPrice: null,
  amount: null,
  category: null,
  relatedTo: null,
  reference: null,
})

const getReferenceSuggestions = (
  options: ExpenseReferenceOptions | undefined,
  relatedTo?: ExpenseRelatedEntityType | null
) => {
  if (!options || !relatedTo) return []
  if (REFERENCE_OPTION_KEYS.includes(relatedTo as keyof ExpenseReferenceOptions)) {
    return options[relatedTo as keyof ExpenseReferenceOptions] ?? []
  }
  return []
}

const columnHeaderSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: 'text.primary',
} as const

const lineFieldLabelSx = {
  fontWeight: 700,
  fontSize: '0.6875rem',
  mb: 0.5,
  color: 'grey.600',
} as const

const numericFieldSx = {
  ...detailTextFieldSx,
  '& .MuiInputBase-input': {
    textAlign: 'right',
  },
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
} as const

const compactLineFieldSx = {
  ...detailTextFieldSx,
  '& .MuiOutlinedInput-root': {
    ...detailTextFieldSx['& .MuiOutlinedInput-root'],
    minHeight: 32,
  },
  '& .MuiInputBase-input': {
    py: '5px',
    fontSize: '0.8125rem',
  },
  '& .MuiSelect-select': {
    py: '5px',
  },
} as const

/** Shared columns: Description | Qty | Unit Price | Amount | delete (row 2). */
const LINE_GRID_COLUMNS = 'minmax(0, 1fr) 88px 100px 100px 36px'

const formatTotalsMoney = (value: number) =>
  `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const PayableViewPage = () => {
  const { expenseId } = useParams()
  const navigate = useNavigate()
  const userId = getUserId()
  const [tabsValue, setTabsValue] = useState(0)
  const [docUrl, setDocUrl] = useState('')
  const [form, setForm] = useState<Partial<Expense>>({})
  const [lineItems, setLineItems] = useState<ExpenseLineItem[]>([])

  const { data: expense, isLoading } = useGetExpenseByIdQuery(
    expenseId ? { expenseId } : skipToken
  )
  // Load reference suggestions async — does not block expense/document UI.
  const { data: referenceOptions } = useGetExpenseReferenceOptionsQuery(
    userId ? { userId } : skipToken
  )
  const [getPreSignedUrl] = useGetPreSignedUrlMutation()
  const [updateExpense, { isLoading: isSaving }] = useUpdateExpenseMutation()
  const [deleteExpense, { isLoading: isDeleting }] = useDeleteExpenseMutation()

  useEffect(() => {
    if (!expense) return
    setForm({
      invoiceNumber: expense.invoiceNumber ?? '',
      name: expense.name ?? '',
      invoiceDate: expense.invoiceDate ?? '',
      dueDate: expense.dueDate ?? '',
      totalAmount: expense.totalAmount ?? null,
      taxAmount: expense.taxAmount ?? null,
      currency: expense.currency ?? 'CAD',
      subtotal: expense.subtotal ?? null,
      note: expense.note ?? '',
    })
    setLineItems(
      (expense.lineItems ?? []).map((item) => ({
        ...item,
        lineNote: item.lineNote ?? null,
        unitPrice: resolveUnitPrice(item.unitPrice, item.amount, item.quantity),
      }))
    )
  }, [expense])

  const loadDocument = useCallback(async () => {
    if (!expense?.s3Path) {
      setDocUrl('')
      return
    }
    try {
      const response = await getPreSignedUrl({
        s3URL: expense.s3Path,
        openInline: true,
      }).unwrap()
      setDocUrl(response.preSignedURL)
    } catch (error) {
      console.error('Failed to load expense document', error)
      setDocUrl('')
    }
  }, [expense?.s3Path, getPreSignedUrl])

  useEffect(() => {
    loadDocument()
  }, [loadDocument])

  const handleClose = () => {
    navigate('/vendor')
  }

  const handleSave = async () => {
    if (!expenseId) return
    const totalAmount = sumLineAmounts(lineItems)
    await updateExpense({
      expenseId,
      body: {
        ...form,
        totalAmount,
        lineItems,
      },
    }).unwrap()
    handleClose()
  }

  const handleDelete = async () => {
    if (!expenseId) return
    await deleteExpense({ expenseId }).unwrap()
    handleClose()
  }

  const updateLine = (index: number, patch: Partial<ExpenseLineItem>) => {
    setLineItems((prev) => {
      const nextItems = prev.map((item, i) => {
        if (i !== index) return item
        const next = { ...item, ...patch }
        if (
          (patch.amount != null || patch.quantity != null) &&
          next.unitPrice == null
        ) {
          next.unitPrice = resolveUnitPrice(
            next.unitPrice,
            next.amount,
            next.quantity
          )
        }
        if (next.unitPrice != null) {
          next.unitPrice = Number(Number(next.unitPrice).toFixed(2))
        }
        if (next.amount != null) {
          next.amount = Number(Number(next.amount).toFixed(2))
        }
        return next
      })
      setForm((formPrev) => ({
        ...formPrev,
        totalAmount: sumLineAmounts(nextItems),
      }))
      return nextItems
    })
  }

  const removeLine = (index: number) => {
    setLineItems((prev) => {
      const nextItems = prev.filter((_, i) => i !== index)
      setForm((formPrev) => ({
        ...formPrev,
        totalAmount: sumLineAmounts(nextItems),
      }))
      return nextItems
    })
  }

  const subTotal = sumLineAmounts(lineItems)
  const taxAmount = form.taxAmount ?? 0
  const grandTotal = Number((subTotal + taxAmount).toFixed(2))
  const currency = form.currency ?? 'CAD'

  const documentPane = docUrl ? (
    <PayableDocumentViewer docUrl={docUrl} s3Path={expense?.s3Path} />
  ) : (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        height: '100%',
        minHeight: 480,
        bgcolor: '#4a4a4a',
        color: 'common.white',
      }}
    >
      <Typography>No document available</Typography>
    </Stack>
  )

  const formPane = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {isLoading || !expense ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <BasicTabs
            tabs={viewTabs}
            value={tabsValue}
            handleChange={(_e, value) => setTabsValue(value)}
            scrollableContent
            onClose={handleClose}
          >
            <TabPanel value={tabsValue} index={0}>
              <Box sx={{ px: 3, py: 2, maxWidth: 560 }}>
                <DetailField label="Invoice#">
                  <TextField
                    fullWidth
                    size="small"
                    value={form.invoiceNumber ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        invoiceNumber: e.target.value,
                      }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
                <DetailField label="Vendor Name">
                  <TextField
                    fullWidth
                    size="small"
                    value={form.name ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
                <DetailField label="Invoice Date">
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={form.invoiceDate ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        invoiceDate: e.target.value,
                      }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
                <DetailField label="Due Date">
                  <TextField
                    type="date"
                    fullWidth
                    size="small"
                    value={form.dueDate ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, dueDate: e.target.value }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
                <DetailField label="Total Amount">
                  <TextField
                    type="number"
                    fullWidth
                    size="small"
                    value={form.totalAmount ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        totalAmount: e.target.value
                          ? Number(e.target.value)
                          : null,
                      }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
                <DetailField label="Tax">
                  <TextField
                    type="number"
                    fullWidth
                    size="small"
                    value={form.taxAmount ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        taxAmount: e.target.value
                          ? Number(e.target.value)
                          : null,
                      }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
                <DetailField label="Note">
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    value={form.note ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, note: e.target.value }))
                    }
                    sx={detailTextFieldSx}
                  />
                </DetailField>
              </Box>
            </TabPanel>
            <TabPanel value={tabsValue} index={1}>
              <Stack spacing={1.5} sx={{ p: 2, pb: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: LINE_GRID_COLUMNS,
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.5,
                    pl: 2.5,
                  }}
                >
                  <Typography sx={columnHeaderSx}>Description</Typography>
                  <Typography sx={{ ...columnHeaderSx, textAlign: 'center' }}>
                    Qty
                  </Typography>
                  <Typography sx={{ ...columnHeaderSx, textAlign: 'center' }}>
                    Unit Price
                  </Typography>
                  <Typography
                    sx={{
                      ...columnHeaderSx,
                      gridColumn: '4 / -1',
                      textAlign: 'right',
                    }}
                  >
                    Amount
                  </Typography>
                </Box>

                {lineItems.map((item, index) => {
                  const suggestions = getReferenceSuggestions(
                    referenceOptions,
                    item.relatedTo
                  )
                  return (
                    <Box
                      key={item.id ?? index}
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: '6px',
                        bgcolor: 'common.white',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          bgcolor: 'primary.main',
                        },
                      }}
                    >
                      <Box sx={{ p: 1.5, pl: 2.5 }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: LINE_GRID_COLUMNS,
                            columnGap: 1,
                            alignItems: 'center',
                          }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            value={item.description ?? ''}
                            onChange={(e) =>
                              updateLine(index, { description: e.target.value })
                            }
                            sx={detailTextFieldSx}
                          />
                          <TextField
                            size="small"
                            type="number"
                            value={item.quantity ?? ''}
                            onChange={(e) =>
                              updateLine(index, {
                                quantity: e.target.value
                                  ? Number(e.target.value)
                                  : null,
                              })
                            }
                            sx={numericFieldSx}
                          />
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01' }}
                            value={formatMoneyDisplay(
                              resolveUnitPrice(
                                item.unitPrice,
                                item.amount,
                                item.quantity
                              )
                            )}
                            onChange={(e) =>
                              updateLine(index, {
                                unitPrice: e.target.value
                                  ? Number(Number(e.target.value).toFixed(2))
                                  : null,
                              })
                            }
                            sx={numericFieldSx}
                          />
                          <TextField
                            size="small"
                            type="number"
                            inputProps={{ step: '0.01' }}
                            value={formatMoneyDisplay(item.amount)}
                            onChange={(e) =>
                              updateLine(index, {
                                amount: e.target.value
                                  ? Number(Number(e.target.value).toFixed(2))
                                  : null,
                              })
                            }
                            sx={{
                              ...numericFieldSx,
                              gridColumn: '4 / -1',
                              '& .MuiInputBase-input': {
                                textAlign: 'right',
                                fontWeight: 700,
                              },
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: LINE_GRID_COLUMNS,
                            columnGap: 1,
                            rowGap: 1,
                            mt: 0.75,
                            alignItems: 'end',
                          }}
                        >
                          <Box
                            sx={{
                              gridColumn: '1 / 3',
                              display: 'grid',
                              gridTemplateColumns: '1.15fr 0.7fr 0.95fr',
                              columnGap: 1,
                              minWidth: 0,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={lineFieldLabelSx}>
                                Category
                              </Typography>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={item.category ?? ''}
                                onChange={(e) =>
                                  updateLine(index, {
                                    category: (e.target.value ||
                                      null) as ExpenseLineItem['category'],
                                  })
                                }
                                sx={compactLineFieldSx}
                              >
                                <MenuItem value="">—</MenuItem>
                                {EXPENSE_CATEGORIES.map((category) => (
                                  <MenuItem key={category} value={category}>
                                    {categoryLabel(category)}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={lineFieldLabelSx}>
                                Related to
                              </Typography>
                              <TextField
                                select
                                size="small"
                                fullWidth
                                value={item.relatedTo ?? ''}
                                onChange={(e) => {
                                  const relatedTo = (e.target.value ||
                                    null) as ExpenseLineItem['relatedTo']
                                  updateLine(index, {
                                    relatedTo,
                                    reference: null,
                                  })
                                }}
                                sx={compactLineFieldSx}
                              >
                                <MenuItem value="">—</MenuItem>
                                {EXPENSE_RELATED_TO.map((related) => (
                                  <MenuItem key={related} value={related}>
                                    {related}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>

                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={lineFieldLabelSx}>
                                Reference
                              </Typography>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={suggestions}
                                value={item.reference ?? ''}
                                onChange={(_event, value) =>
                                  updateLine(index, {
                                    reference:
                                      typeof value === 'string'
                                        ? value
                                        : value ?? null,
                                  })
                                }
                                onInputChange={(_event, value, reason) => {
                                  if (reason === 'input') {
                                    updateLine(index, {
                                      reference: value || null,
                                    })
                                  }
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    sx={compactLineFieldSx}
                                  />
                                )}
                              />
                            </Box>
                          </Box>

                          <Box sx={{ gridColumn: '3 / 5', minWidth: 0 }}>
                            <Typography sx={lineFieldLabelSx}>Notes</Typography>
                            <TextField
                              size="small"
                              fullWidth
                              value={item.lineNote ?? ''}
                              onChange={(e) =>
                                updateLine(index, {
                                  lineNote: e.target.value || null,
                                })
                              }
                              sx={compactLineFieldSx}
                            />
                          </Box>

                          <IconButton
                            aria-label="Delete line"
                            size="small"
                            onClick={() => removeLine(index)}
                            sx={{
                              width: 32,
                              height: 32,
                              mb: '1px',
                              borderRadius: '6px',
                              color: 'error.main',
                              bgcolor: 'error.50',
                              '&:hover': { bgcolor: 'error.100' },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  )
                })}

                <Button
                  variant="outlined"
                  sx={{
                    borderStyle: 'dashed',
                    alignSelf: 'stretch',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '6px',
                  }}
                  onClick={() => setLineItems((prev) => [...prev, emptyLine()])}
                >
                  + Add a Line
                </Button>
              </Stack>
            </TabPanel>
          </BasicTabs>
          {tabsValue === 1 && (
            <Box
              sx={{
                flexShrink: 0,
                px: 3.5,
                py: 1.5,
                bgcolor: 'grey.50',
                borderTop: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: LINE_GRID_COLUMNS,
                  columnGap: 1,
                  alignItems: 'center',
                  py: 0.5,
                }}
              >
                <Typography
                  sx={{
                    gridColumn: '1 / 4',
                    textAlign: 'right',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    pr: 1,
                  }}
                >
                  {`Total (${currency})`}
                </Typography>
                <Typography
                  sx={{
                    gridColumn: '4 / -1',
                    textAlign: 'right',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatTotalsMoney(grandTotal)}
                </Typography>
              </Box>
            </Box>
          )}
          <ActionButtons
            firstBtnProps={{
              title: 'Save',
              onClick: handleSave,
              disabled: isSaving,
            }}
            secondBtnProps={{
              title: 'Delete',
              onClick: handleDelete,
              disabled: isDeleting,
              bgcolor: 'error.main',
            }}
          />
        </>
      )}
    </Box>
  )

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '95vw' },
          maxWidth: '95vw',
          height: { xs: '100%', sm: '95vh' },
          maxHeight: { xs: '100%', sm: '95vh' },
          m: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <TwoEqualColumnLayout>
            {documentPane}
            {formPane}
            {formPane}
          </TwoEqualColumnLayout>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default PayableViewPage
